import React, {FC, useState} from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import {DataGrid, GridRenderCellParams, GridRowParams} from '@mui/x-data-grid';
import SearchBox from '@geomatico/geocomponents/Search/SearchBox';

import {useTranslation} from 'react-i18next';

import {PROJECT_STAGE_CHIP_CONFIGURATION} from '@/config';
import {Project} from '@/domain/entities/Project';
import {TFunction} from 'i18next';
import { esES } from '@mui/x-data-grid/locales';

const columns = (t: TFunction) => [
  {field: 'zone', width: 350, flex: 1, 
    renderHeader: () => <Typography sx={styles.header} variant="h6">{t('projects.zone')}</Typography>,
    renderCell: (params: GridRenderCellParams) => <Typography sx={styles.header} variant="overline">{params.value}</Typography>},
  {field: 'stage', width: 250, flex: 1, 
    renderHeader: () => <Typography sx={styles.header} variant="h6">{t('projects.stage')}</Typography>,
    valueGetter: (params: GridRenderCellParams) => t(`stage.${params}`),
    renderCell: (params: GridRenderCellParams) => {
      if (!params.value) return null;
      const key = params.row.stage.toUpperCase() as keyof typeof PROJECT_STAGE_CHIP_CONFIGURATION;
      const [backgroundColor, color, variantStr] = PROJECT_STAGE_CHIP_CONFIGURATION[key] ?? ['#fabada', '#000', 'filled'];
      const variant: 'filled' | 'outlined' = variantStr as 'filled' | 'outlined';
      return <Chip
        size="small"
        label={t(`stage.${params.row.stage}`).toUpperCase()}
        variant={variant}
        sx={{ backgroundColor, color, minWidth: '120px', fontWeight: 700}}
      />;
    }
  },
];

//TYPES
export type ProjectTableProps = {
   projects?: Array<Project>,
   onSelectedProject: (projectUuid: string) => void,
   errorMessage?: string
};

const ProjectTable: FC<ProjectTableProps> = ({projects, onSelectedProject, errorMessage}) => {

  const {t} = useTranslation();
  const [searchedText, setSearchedText] = useState<string>('');
  
  const filteredProjects = projects
    ? projects
      .filter(project => project.zone.toUpperCase()
        .includes(searchedText))
    : [];

  const rows = filteredProjects
    .sort((a, b) => a.zone.localeCompare(b.zone))
    .map(project => ({
      id: project.uuid,
      uuid: project.uuid,
      zone: project.zone,
      stage: project.stage
    }));

  const handleRowClick = (params: GridRowParams) => onSelectedProject(params.row.uuid);
  const handleSearchedText = (text: string) => setSearchedText(text.toUpperCase());
  
  return <Box sx={styles.container}>
    {errorMessage && <Typography color="error">{errorMessage}</Typography>}
    {
      projects && projects.length > 1 &&
      <SearchBox text={searchedText} onSearchClick={() => {}} onTextChange={handleSearchedText} sx={styles.searchBox}/>
    }
    <DataGrid
      rows={rows}
      columns={columns(t)}
      onRowClick={handleRowClick}
      hideFooter
      disableColumnSelector
      sx={styles.table}
      localeText={{
        ...esES.components.MuiDataGrid.defaultProps.localeText,
        noRowsLabel: t('projects.withoutProjects')
      }}
    />
    {projects && projects.length > 0 && <Typography>{t('projects.selectForInformation')}</Typography>}
  </Box>;
};
export default ProjectTable;

const styles = {
  container: {
    position: 'absolute',
    top: 100,
    display: 'flex',
    flexDirection: 'column',
    width: '75%',
    gap: 2,
    maxHeight: '80vh'
  },
  searchBox: {
    '&.SearchBox-root': {
      width: '50%'
    }
  },
  table: {
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle': {
      color: 'common.white',
      fontWeight: 900,
      backgroundColor: 'primary.main',
    },
    '& .MuiDataGrid-cell:focus': { outline: 'none' },
    '& .MuiDataGrid-columnHeader--sortable .MuiSvgIcon-root': {
      color: 'white', // color de los iconos de ordenar
    },
    '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
      color: 'white', // color del icono del menú/filtro
    }
  },
  header: {
    fontWeight: 900
  }
};